import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from './ui.tsx';

const carriesFiles = (e: DragEvent | React.DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

/**
 * A place to drop files, plainly marked (Jason, 2026-10-03: "there should be
 * a drop zone very clearly shown"): a dashed rule round a few words and a
 * button to choose them instead. The moment files are dragged over the
 * window it says where to let go; over it, it goes ink. A file let go
 * anywhere else on the page is ignored rather than opened by the browser in
 * place of the app.
 */
export function DropZone({
  onFiles,
  label,
  hint,
  accept,
  busy = false,
  compact = false,
  tone = 'plain',
  children,
}: {
  onFiles: (files: File[]) => void;
  label: string;
  hint?: ReactNode;
  accept?: string;
  busy?: boolean;
  compact?: boolean;
  /** Dropped into something already shared (design study 29): ruled in cedar, so it reads as not a draft's. */
  tone?: 'plain' | 'shared';
  children?: ReactNode;
}) {
  const picker = useRef<HTMLInputElement>(null);
  const [armed, setArmed] = useState(false);
  const [over, setOver] = useState(false);
  const depth = useRef(0);

  // Files in the air anywhere over the window: show where they go, and never let the browser open one dropped beside it.
  useEffect(() => {
    let inWindow = 0;
    const enter = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      inWindow++;
      setArmed(true);
    };
    const leave = (e: DragEvent) => {
      if (!carriesFiles(e)) return;
      inWindow = Math.max(0, inWindow - 1);
      if (!inWindow) setArmed(false);
    };
    const hold = (e: DragEvent) => {
      if (carriesFiles(e)) e.preventDefault();
    };
    const done = (e: DragEvent) => {
      if (carriesFiles(e)) e.preventDefault();
      inWindow = 0;
      setArmed(false);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', hold);
    window.addEventListener('drop', done);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', hold);
      window.removeEventListener('drop', done);
    };
  }, []);

  return (
    <div
      onDragEnter={(e) => {
        if (!carriesFiles(e)) return;
        depth.current++;
        setOver(true);
      }}
      onDragOver={(e) => {
        if (!carriesFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (!depth.current) setOver(false);
      }}
      onDrop={(e) => {
        if (!carriesFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        setArmed(false);
        const files = [...e.dataTransfer.files];
        if (files.length) onFiles(files);
      }}
      className={`flex flex-col items-center justify-center gap-3 rounded-[3px] border-2 border-dashed text-center transition-colors duration-[120ms] ${
        compact ? 'min-h-24 px-4 py-4' : 'min-h-40 px-6 py-8'
      } ${over ? 'border-ink bg-inset' : armed ? 'border-ink bg-sheet' : tone === 'shared' ? 'border-cedar/70 bg-sheet/60' : 'border-rule-strong bg-sheet/60'}`}
    >
      <p className={compact ? 'text-[1rem]' : 'font-display text-[1.25rem] sm:text-[1.375rem]'}>{over ? 'Let go to add them' : armed ? 'Drop them here' : label}</p>
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
        <Button tone="secondary" busy={busy} onClick={() => picker.current?.click()}>
          Choose files
        </Button>
        {children}
      </div>
      {hint ? <div className="meta max-w-measure">{hint}</div> : null}
      <input
        ref={picker}
        type="file"
        multiple
        accept={accept}
        className="sr-only"
        aria-label={label}
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          if (files.length) onFiles(files);
        }}
      />
    </div>
  );
}
